import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../utils/api';

const FirmPage = () => {
  const { firmId } = useParams();
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({ name: '', phone: '', comment: '' });
  const [complaintForm, setComplaintForm] = useState({ name: '', phone: '', comment: '' });
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showComplaintForm, setShowComplaintForm] = useState(false);

  useEffect(() => {
    const loadFirm = async () => {
      setLoading(true);
      try {
        const firmData = await api.getFirm(firmId);
        setFirm(firmData);
      } catch (error) {
        console.error('Error loading firm:', error);
      }
      setLoading(false);
    };
    loadFirm();
  }, [firmId]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.addComment(firmId, commentForm);
      alert('Yorumunuz başarıyla gönderildi. Onaylandıktan sonra yayınlanacaktır.');
      setCommentForm({ name: '', phone: '', comment: '' });
      setShowCommentForm(false);
    } catch (error) {
      alert('Yorum gönderilirken bir hata oluştu.');
    }
  };

  const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.addComplaint(firmId, complaintForm);
      alert('Şikayetiniz başarıyla gönderildi.');
      setComplaintForm({ name: '', phone: '', comment: '' });
      setShowComplaintForm(false);
    } catch (error) {
      alert('Şikayet gönderilirken bir hata oluştu.');
    }
  };

  const generateJsonLd = () => {
    if (!firm) return null;
    
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": firm.title,
      "description": firm.services,
      "telephone": firm.kamuTelefon,
      "email": firm.kamuEmail,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": `${firm.sokak} ${firm.apt} ${firm.daire}`,
        "addressLocality": firm.mahalle,
        "addressRegion": firm.sehir,
        "addressCountry": "TR"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": firm.puan,
        "reviewCount": firm.yorumlar?.length || 0
      }
    };

    return (
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    );
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: 'var(--gray-600)' }}>Yükleniyor...</div>
      </div>
    );
  }

  if (!firm) {
    return (
      <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--navy-blue)', marginBottom: '16px' }}>Firma Bulunamadı</h2>
        <p style={{ color: 'var(--gray-600)' }}>Aradığınız firma mevcut değil.</p>
      </div>
    );
  }

  const headerStyle = {
    background: 'linear-gradient(135deg, var(--navy-blue) 0%, var(--baby-blue) 100%)',
    color: 'var(--white)',
    padding: '60px 0'
  };

  const contentStyle = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '40px',
    marginTop: '40px'
  };

  const sectionStyle = {
    background: 'var(--white)',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    marginBottom: '24px'
  };

  const formStyle = {
    display: 'grid',
    gap: '16px',
    marginTop: '16px'
  };

  return (
    <div>
      {generateJsonLd()}
      
      {/* Header */}
      <section style={headerStyle}>
        <div className="container">
          <h1 style={{ fontSize: '36px', marginBottom: '16px', fontFamily: 'Comfortaa, cursive' }}>
            {firm.title}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <span style={{ fontSize: '20px' }}>⭐ {firm.puan}</span>
            <span style={{ opacity: 0.9 }}>({firm.yorumlar?.length || 0} yorum)</span>
          </div>
          <p style={{ fontSize: '18px', opacity: 0.9 }}>{firm.services}</p>
        </div>
      </section>

      <div className="container" style={{ padding: '0 20px 60px' }}>
        <div style={window.innerWidth > 768 ? contentStyle : { display: 'block' }}>
          {/* Main Content */}
          <div>
            {/* Firm Details */}
            <div style={sectionStyle}>
              <h2 style={{ fontSize: '24px', marginBottom: '20px', color: 'var(--navy-blue)', fontFamily: 'Comfortaa, cursive' }}>
                İletişim Bilgileri
              </h2>
              <div style={{ display: 'grid', gap: '12px' }}>
                <div>📞 <strong>Telefon:</strong> {firm.kamuTelefon}</div>
                {firm.kamuEmail && <div>📧 <strong>E-posta:</strong> {firm.kamuEmail}</div>}
                <div>📍 <strong>Adres:</strong> {firm.acikAdres}</div>
                <div>🏙️ <strong>Şehir:</strong> {firm.sehir}</div>
                <div>🏘️ <strong>Mahalle:</strong> {firm.mahalle}</div>
              </div>
            </div>

            {/* Comments Section */}
            <div style={sectionStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '24px', color: 'var(--navy-blue)', fontFamily: 'Comfortaa, cursive' }}>
                  Yorumlar ({firm.yorumlar?.length || 0})
                </h2>
                <button 
                  onClick={() => setShowCommentForm(!showCommentForm)}
                  className="btn btn-secondary"
                >
                  Yorum Yap
                </button>
              </div>

              {showCommentForm && (
                <form onSubmit={handleCommentSubmit} style={formStyle}>
                  <input
                    type="text"
                    placeholder="Adınız Soyadınız"
                    value={commentForm.name}
                    onChange={(e) => setCommentForm({...commentForm, name: e.target.value})}
                    className="form-input"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Telefon Numaranız"
                    value={commentForm.phone}
                    onChange={(e) => setCommentForm({...commentForm, phone: e.target.value})}
                    className="form-input"
                    required
                  />
                  <textarea
                    placeholder="Yorumunuz"
                    value={commentForm.comment}
                    onChange={(e) => setCommentForm({...commentForm, comment: e.target.value})}
                    className="form-textarea"
                    required
                  />
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="submit" className="btn btn-primary">Gönder</button>
                    <button 
                      type="button" 
                      onClick={() => setShowCommentForm(false)}
                      className="btn btn-secondary"
                    >
                      İptal
                    </button>
                  </div>
                </form>
              )}

              <div style={{ marginTop: '24px' }}>
                {firm.yorumlar && firm.yorumlar.length > 0 ? (
                  firm.yorumlar.map((yorum, index) => (
                    <div key={index} style={{
                      background: 'var(--light-blue)',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}>
                      <p style={{ color: 'var(--gray-800)' }}>{yorum}</p>
                    </div>
                  ))
                ) : (
                  <p style={{ color: 'var(--gray-600)', fontStyle: 'italic' }}>
                    Henüz yorum yapılmamış. İlk yorumu siz yapın!
                  </p>
                )}
              </div>
            </div>

            {/* Complaint Section */}
            <div style={sectionStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '24px', color: 'var(--navy-blue)', fontFamily: 'Comfortaa, cursive' }}>
                  Şikayet Et
                </h2>
                <button 
                  onClick={() => setShowComplaintForm(!showComplaintForm)}
                  className="btn"
                  style={{ backgroundColor: '#dc2626', color: 'white' }}
                >
                  Şikayet Et
                </button>
              </div>

              {showComplaintForm && (
                <form onSubmit={handleComplaintSubmit} style={formStyle}>
                  <input
                    type="text"
                    placeholder="Adınız Soyadınız"
                    value={complaintForm.name}
                    onChange={(e) => setComplaintForm({...complaintForm, name: e.target.value})}
                    className="form-input"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Telefon Numaranız"
                    value={complaintForm.phone}
                    onChange={(e) => setComplaintForm({...complaintForm, phone: e.target.value})}
                    className="form-input"
                    required
                  />
                  <textarea
                    placeholder="Şikayetiniz"
                    value={complaintForm.comment}
                    onChange={(e) => setComplaintForm({...complaintForm, comment: e.target.value})}
                    className="form-textarea"
                    required
                  />
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      type="submit" 
                      className="btn"
                      style={{ backgroundColor: '#dc2626', color: 'white' }}
                    >
                      Gönder
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowComplaintForm(false)}
                      className="btn btn-secondary"
                    >
                      İptal
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Quick Actions */}
            <div style={sectionStyle}>
              <h3 style={{ fontSize: '20px', marginBottom: '16px', color: 'var(--navy-blue)', fontFamily: 'Comfortaa, cursive' }}>
                Hızlı İşlemler
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                <a href={`tel:${firm.kamuTelefon}`} className="btn btn-primary" style={{ textAlign: 'center' }}>
                  📞 Hemen Ara
                </a>
                {firm.kamuEmail && (
                  <a href={`mailto:${firm.kamuEmail}`} className="btn btn-secondary" style={{ textAlign: 'center' }}>
                    📧 E-posta Gönder
                  </a>
                )}
              </div>
            </div>

            {/* Map Section */}
            <div style={sectionStyle}>
              <h3 style={{ fontSize: '20px', marginBottom: '16px', color: 'var(--navy-blue)', fontFamily: 'Comfortaa, cursive' }}>
                Konum
              </h3>
              <div style={{
                background: 'var(--light-blue)',
                height: '200px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--navy-blue)',
                fontSize: '16px'
              }}>
                🗺️ Harita Yükleniyor...
              </div>
              <p style={{ fontSize: '14px', color: 'var(--gray-600)', marginTop: '12px' }}>
                {firm.acikAdres}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmPage;